/**
 * Utility functions for search operations
 */

/**
 * Searches for a needle in an array of strings (case-insensitive)
 * @param haystack - array of strings to search in
 * @param needle - search query
 * @returns true if needle is found in any of the strings
 */
export const searchInArray = (haystack: (string | undefined | null)[], needle: string): boolean => {
  if (!needle.trim()) return true;

  const normalizedNeedle = needle.toLowerCase().trim();

  return haystack.some((item) => {
    if (!item) return false;
    return item.toLowerCase().includes(normalizedNeedle);
  });
};

/**
 * Searches for a needle in a single string (case-insensitive)
 * @param haystack - string to search in
 * @param needle - search query
 * @returns true if needle is found in the string
 */
export const searchInString = (haystack: string | undefined | null, needle: string): boolean => {
  if (!needle.trim()) return true;
  if (!haystack) return false;

  const normalizedNeedle = needle.toLowerCase().trim();
  return haystack.toLowerCase().includes(normalizedNeedle);
};

/**
 * Advanced search function that searches across multiple fields
 * @param item - object to search in
 * @param searchFields - array of field names to search in
 * @param needle - search query
 * @returns true if needle is found in any of the specified fields
 */
export const searchInObject = <T extends object>(
  item: T,
  searchFields: (keyof T)[],
  needle: string,
): boolean => {
  if (!needle.trim()) return true;

  const normalizedNeedle = needle.toLowerCase().trim();

  return searchFields.some((field) => {
    const value = item[field];
    if (value === undefined || value === null) return false;

    if (typeof value === 'string') {
      return value.toLowerCase().includes(normalizedNeedle);
    }

    if (Array.isArray(value)) {
      return value.some(
        (item: unknown) =>
          typeof item === 'string' && item.toLowerCase().includes(normalizedNeedle),
      );
    }

    return false;
  });
};

/**
 * Specialized search function for teams
 * @param team - team object to search in
 * @param needle - search query
 * @returns true if needle is found in team name, description, or lead name
 */
export const searchInTeam = (
  team: { name: string; description?: string; lead?: { name: string } },
  needle: string,
): boolean => {
  const searchFields = [team.name, team.description, team.lead?.name];

  return searchInArray(searchFields, needle);
};
